import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, Copy, Crown, Loader2, Mail, Trash2, UserPlus, AlertTriangle, Link2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { apiError } from "../lib/auth";
import { useSession } from "../lib/session";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { CONTACT_EMAIL } from "../marketing/content";

const inputCls = "w-full bg-slate-950/60 border border-slate-800 rounded-xl px-4 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-indigo-500/60";

const ROLE_LABEL = { owner: "Owner", admin: "Admin", editor: "Editor" };
const ROLE_HELP = {
  owner: "Everything, including deleting the workspace",
  admin: "Manages the team and settings",
  editor: "Creates, edits and approves content",
};

const LIMITS = [
  ["posts_per_month", "Posts / month"], ["images_per_month", "Images / month"], ["brands", "Brands"],
  ["feeds", "Feeds"], ["accounts", "Accounts"], ["seats", "Seats"], ["wp_sites", "WordPress sites"],
];

const inviteUrl = (token) => `${window.location.origin}/invite/${token}`;

const CopyButton = ({ text }) => {
  const [done, setDone] = useState(false);
  return (
    <button onClick={async () => { await navigator.clipboard.writeText(text); setDone(true); setTimeout(() => setDone(false), 1500); }}
      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-slate-700 text-xs font-semibold text-slate-300 hover:border-slate-600">
      {done ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />} {done ? "Copied" : "Copy link"}
    </button>
  );
};

export const TeamView = () => {
  const { me, refresh, canManage } = useSession();
  const [data, setData] = useState(null);
  const [invite, setInvite] = useState({ email: "", role: "editor" });
  const [inviting, setInviting] = useState(false);
  const [fresh, setFresh] = useState(null);
  const [name, setName] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [confirm, setConfirm] = useState("");

  const load = async () => {
    const { data } = await api.get("/workspace");
    setData(data);
    setName(data.workspace.name);
  };
  useEffect(() => { load().catch(() => toast.error("Could not load the team")); }, []);

  const sendInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      const { data: inv } = await api.post("/workspace/invites", invite);
      setFresh(inv);
      setInvite({ email: "", role: "editor" });
      load();
    } catch (err) {
      toast.error(apiError(err, "Could not create the invite"));
    } finally {
      setInviting(false);
    }
  };

  const setRole = async (userId, role) => {
    try {
      await api.patch(`/workspace/members/${userId}`, { role });
      toast.success("Role updated");
      load();
    } catch (err) { toast.error(apiError(err, "Could not change the role")); }
  };

  const remove = async (userId) => {
    try {
      await api.delete(`/workspace/members/${userId}`);
      toast.success("Removed from the workspace");
      load();
    } catch (err) { toast.error(apiError(err, "Could not remove them")); }
  };

  const rename = async () => {
    try {
      await api.patch("/workspace", { name });
      toast.success("Workspace renamed");
      refresh();
    } catch (err) { toast.error(apiError(err, "Could not rename")); }
  };

  const destroy = async () => {
    try {
      await api.delete("/workspace", { data: { confirm_name: confirm } });
      toast.success("Workspace deleted");
      window.location.assign("/login");
    } catch (err) { toast.error(apiError(err, "Could not delete the workspace")); }
  };

  if (!data) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 text-indigo-400 animate-spin" /></div>;
  const plan = me?.plan || {};
  const usage = me?.usage || {};
  const usedFor = (k) => ({ posts_per_month: usage.posts, images_per_month: usage.images }[k] ?? usage[k] ?? 0);

  return (
    <div className="space-y-8" data-testid="team-view">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-indigo-300">Workspace</p>
        <h1 className="font-heading text-4xl font-extrabold tracking-tight text-slate-50 mt-2">Team &amp; plan</h1>
        <p className="text-slate-400 mt-2 max-w-2xl">Who works in {data.workspace.name}, what they can do, and what the plan allows.</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1.6fr_1fr]">
        {/* Members */}
        <div className="glass rounded-2xl p-6 space-y-5">
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-bold text-slate-100">Members</h3>
            <span className="text-xs text-slate-500">{data.members.length} of {plan.seats ?? "∞"} seats</span>
          </div>
          <div className="divide-y divide-slate-800/80">
            {data.members.map((m) => (
              <div key={m.id} className="flex items-center gap-3 py-3">
                <span className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500/40 to-fuchsia-500/40 flex items-center justify-center text-sm font-bold text-white">
                  {(m.name || m.email || "?")[0].toUpperCase()}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-slate-100 truncate flex items-center gap-1.5">
                    {m.name || m.email}{m.workspace_role === "owner" && <Crown className="w-3.5 h-3.5 text-amber-300" />}
                    {m.id === me?.user?.id && <span className="text-[10px] text-slate-500 font-normal">(you)</span>}
                  </p>
                  <p className="text-xs text-slate-500 truncate">{m.email}</p>
                </div>
                {canManage && m.id !== me?.user?.id ? (
                  <>
                    <select value={m.workspace_role} onChange={(e) => setRole(m.id, e.target.value)}
                      className="bg-slate-950/60 border border-slate-800 rounded-lg px-2 py-1.5 text-xs text-slate-200">
                      {Object.entries(ROLE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                    <button onClick={() => remove(m.id)} title="Remove" className="p-1.5 text-slate-500 hover:text-rose-300">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </>
                ) : (
                  <span className="text-xs font-semibold text-slate-400">{ROLE_LABEL[m.workspace_role]}</span>
                )}
              </div>
            ))}
          </div>

          {canManage && (
            <form onSubmit={sendInvite} className="rounded-xl border border-slate-800 bg-slate-950/30 p-4 space-y-3">
              <p className="text-sm font-semibold text-slate-200 flex items-center gap-2"><UserPlus className="w-4 h-4 text-indigo-300" /> Invite someone</p>
              <div className="grid sm:grid-cols-[1fr_auto_auto] gap-2">
                <div className="relative">
                  <Mail className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input type="email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })}
                    placeholder="Email (or leave blank for an open link)" className={`${inputCls} pl-9`} />
                </div>
                <select value={invite.role} onChange={(e) => setInvite({ ...invite, role: e.target.value })}
                  className="bg-slate-950/60 border border-slate-800 rounded-xl px-3 py-2.5 text-sm text-slate-200">
                  <option value="editor">Editor</option>
                  <option value="admin">Admin</option>
                </select>
                <button disabled={inviting} className="px-4 py-2.5 rounded-xl bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-semibold flex items-center justify-center gap-2">
                  {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create invite"}
                </button>
              </div>
              <p className="text-[11px] text-slate-600">{ROLE_HELP[invite.role]}. Links expire in 7 days and work once.</p>
              {fresh && (
                <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-2 rounded-lg border border-emerald-500/25 bg-emerald-500/10 px-3 py-2">
                  <Link2 className="w-4 h-4 text-emerald-300 shrink-0" />
                  <code className="text-xs text-emerald-100 truncate flex-1">{inviteUrl(fresh.token)}</code>
                  <CopyButton text={inviteUrl(fresh.token)} />
                </motion.div>
              )}
            </form>
          )}

          {data.invites.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Pending invites</p>
              <div className="space-y-2">
                {data.invites.map((inv) => (
                  <div key={inv.token} className="flex items-center gap-2 text-sm">
                    <span className="text-slate-300 truncate flex-1">{inv.email || "Open link"} · <span className="text-slate-500">{ROLE_LABEL[inv.role]}</span></span>
                    <CopyButton text={inviteUrl(inv.token)} />
                    {canManage && (
                      <button onClick={async () => { await api.delete(`/workspace/invites/${inv.token}`); load(); }}
                        className="p-1.5 text-slate-500 hover:text-rose-300" title="Revoke"><Trash2 className="w-4 h-4" /></button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Plan and settings */}
        <div className="space-y-6">
          <div className="glass rounded-2xl p-6">
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-bold text-slate-100">Plan</h3>
              <span className="text-xs font-bold uppercase tracking-wider px-2 py-1 rounded-md bg-indigo-500/15 text-indigo-200">{plan.label}</span>
            </div>
            <div className="mt-4 space-y-2.5">
              {LIMITS.map(([k, label]) => (
                <div key={k} className="flex justify-between text-sm">
                  <span className="text-slate-400">{label}</span>
                  <span className="text-slate-200 font-medium">{usedFor(k)} <span className="text-slate-500">/ {plan[k] ?? "∞"}</span></span>
                </div>
              ))}
              {[["autopilot", "Autopilot"], ["auto_approve", "Auto-approval"]].map(([k, label]) => (
                <div key={k} className="flex justify-between text-sm">
                  <span className="text-slate-400">{label}</span>
                  <span className={plan[k] ? "text-emerald-300" : "text-slate-500"}>{plan[k] ? "Included" : "Not included"}</span>
                </div>
              ))}
            </div>
            {plan.key !== "unlimited" && (
              <p className="mt-5 text-xs text-slate-500 leading-relaxed">
                Need more? Write to <a href={`mailto:${CONTACT_EMAIL}`} className="text-indigo-300 hover:text-indigo-200">{CONTACT_EMAIL}</a> and we'll move you up.
              </p>
            )}
          </div>

          {canManage && (
            <div className="glass rounded-2xl p-6 space-y-3">
              <h3 className="font-heading font-bold text-slate-100">Workspace name</h3>
              <div className="flex gap-2">
                <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
                <button onClick={rename} disabled={name === data.workspace.name}
                  className="px-4 rounded-xl bg-slate-800 hover:bg-slate-700 text-sm font-semibold text-slate-100 disabled:opacity-50">Save</button>
              </div>
            </div>
          )}

          {data.your_role === "owner" && data.workspace.plan !== "unlimited" && (
            <div className="rounded-2xl border border-rose-900/50 bg-rose-950/10 p-6">
              <h3 className="font-heading font-bold text-rose-200 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Delete workspace</h3>
              <p className="mt-2 text-sm text-slate-400">Removes every post, brand, feed, article and connected account in it. This cannot be undone.</p>
              <button onClick={() => setDeleting(true)} className="mt-4 px-4 py-2 rounded-xl border border-rose-800 text-rose-200 text-sm font-semibold hover:bg-rose-950/40">
                Delete this workspace
              </button>
            </div>
          )}
        </div>
      </div>

      <Dialog open={deleting} onOpenChange={setDeleting}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-rose-200">Delete {data.workspace.name}?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-400">Type the workspace name to confirm.</p>
          <input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={data.workspace.name} className={inputCls} />
          <button onClick={destroy} disabled={confirm !== data.workspace.name}
            className="w-full py-2.5 rounded-xl bg-rose-600 hover:bg-rose-500 disabled:opacity-40 text-white text-sm font-semibold">
            Delete permanently
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
};
